import {
  Anchor,
  Badge,
  Button,
  Flex,
  Group,
  HoverCard,
  LoadingOverlay,
  NumberInput,
  Paper,
  SegmentedControl,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from "@mantine/core";
import { useLanguage } from "../../../../../stores/LanguageStore";
import { useProfileStore } from "../../../../../stores/UserStore";
import {
  IconArrowRight,
  IconCheck,
  IconDeviceMobile,
  IconExclamationCircle,
  IconExternalLink,
  IconInfoCircle,
  IconShoppingCartCheck,
  IconUserSearch,
} from "@tabler/icons-react";
import { IconLinkedin } from "../../../../../components/Icons/IconLinkedin";
import Price from "../../../newPricing/Price";
import { parseDateFuture } from "../../../../../functions/dates";
import { IconEmail } from "../../../../../assets/icons/IconEmail";
import { Link } from "react-router-dom";
import BillingChanged from "./BillingChanged";
import PaymentError from "../PaymentError";

const PaidElement = ({
  loading,
  reccurence,
  setRecurrence,
  linkedin,
  setLinkedin,
  email,
  setEmail,
  enrich,
  setEnrich,
  mobile,
  setMobile,
  price,
  handleCheckout,
  defaultValues,
  defaultPrice,
  setDefaultValues,
  defaultValuesStart,
  edited,
  setEdited,
  productsMap,
}) => {
  const lang = useLanguage((s) => s.language);
  const profile = useProfileStore((s) => s.profile);

  return (
    <Stack gap={0} mt={8}>
      <PaymentError w={250} right={"6%"} top={228} />
      <Stack maw={850}>
        {profile?.subscriptions?.next_monthly_amount !==
          profile?.subscriptions?.monthly_amount && (
          <BillingChanged productsMap={productsMap} />
        )}

        {profile?.subscriptions?.payment_methods?.length > 0 &&
          !profile?.subscriptions?.payment_method_valid && (
            <Flex
              bg={"red.8"}
              p={"md"}
              style={{ borderRadius: 8 }}
              justify={"center"}
              c={"white"}
              align={"center"}
              gap={"lg"}
            >
              <ThemeIcon color="white" size={"lg"} my={-4}>
                <IconExclamationCircle size={32} />
              </ThemeIcon>
              <Text>
                {lang.pricing.payment_method_not_valid}{" "}
                <Anchor
                  component={Link}
                  to={"/settings/subscription/billing"}
                  c="white"
                  fw={800}
                >
                  {lang.pricing.update_now}
                  <IconExternalLink
                    size={16}
                    style={{ marginBottom: -3, marginLeft: 4 }}
                  />
                </Anchor>
              </Text>
            </Flex>
          )}
      </Stack>

      <Title order={4} mt={16} mb={4} ta={"center"} maw={850}>
        {lang.settings.actual_plan}
      </Title>

      <Paper
        p={"lg"}
        component={Stack}
        mt={"sm"}
        w={"100%"}
        maw={850}
        justify="center"
        pos={"relative"}
        style={{ boxShadow: "#00000012 0 2px 6px" }}
      >
        <LoadingOverlay visible={loading} style={{ borderRadius: 24 }} />

        <Title order={4} ta={"center"}>
          {lang.pricing.title}
        </Title>

        <Flex
          bg={"red.2"}
          p={"md"}
          style={{ borderRadius: 8 }}
          justify={"center"}
        >
          <Text size="md" ta={"center"}>
            {profile?.subscriptions?.is_canceled ? (
              <>
                {lang.pricing.cancel_request}
                <b>
                  {parseDateFuture(profile?.subscriptions?.canceled_at)}
                </b>{" "}
                <br />
                {lang.pricing.will_end}
                <b>{parseDateFuture(profile?.subscriptions?.end_date)}</b>{" "}
                <br />
              </>
            ) : profile?.subscriptions?.end_date ? (
              <>
                {lang.pricing.premium.will_end}{" "}
                <b>{parseDateFuture(profile?.subscriptions?.end_date)}</b>
              </>
            ) : (
              <>{lang.pricing.free.ended}</>
            )}
          </Text>
        </Flex>

        {/* <Text ta={"center"}>{lang.pricing.title_tooltip}</Text> */}

        {/* <Title order={4} ta={"center"}>
          {lang.pricing.premium.title}
        </Title> */}

        <SegmentedControl
          data={[
            {
              value: "monthly",
              label: (
                <Group w={"100%"} justify="center">
                  <Text fw={500}>{lang.pricing.monthly}</Text>
                </Group>
              ),
            },
            {
              value: "annual",
              label: (
                <Group w={"100%"} justify="center" pos={"relative"}>
                  <Text fw={500}>{lang.pricing.annual}</Text>

                  <Badge color="primary" size="md" mr={-18}>
                    -20%
                  </Badge>
                </Group>
              ),
            },
          ]}
          w={600}
          mx={"auto"}
          size="lg"
          value={reccurence}
          onChange={setRecurrence}
          disabled={profile?.subscriptions?.active === true}
        />

        {/* Fields */}
        <Group gap={"xl"} mt={16}>
          <Stack gap={"xl"}>
            <Group
              wrap="nowrap"
              gap={"xs"}
              bg={"gray.1"}
              style={{ borderRadius: 12 }}
              p={"md"}
            >
              <Stack w={250} gap={4}>
                <Group>
                  <ThemeIcon variant="subtle">
                    <IconLinkedin size={24} />
                  </ThemeIcon>
                  <Text mr={8} fw={600}>
                    {lang.pricing.linkedin_accounts}
                  </Text>
                </Group>
              </Stack>
              <NumberInput
                value={defaultValues?.linkedin || linkedin}
                onChange={(v) => {
                  setDefaultValues((prev) => ({ ...prev, linkedin: 0 }));
                  setLinkedin(v);

                  // Check if same value, then remove it in edited
                  if (!defaultValuesStart?.linkedin) {
                    return setEdited((prev) => [...prev, "linkedin"]);
                  }
                  if (
                    defaultValuesStart?.linkedin &&
                    v !== defaultValuesStart?.linkedin
                  ) {
                    return setEdited((prev) => [...prev, "linkedin"]);
                  }
                  if (v === defaultValuesStart?.linkedin) {
                    setEdited((prev) => prev.filter((x) => x !== "linkedin"));
                  }
                }}
                w={100}
                ml={"auto"}
                min={0}
                max={1000}
                size="md"
              />
            </Group>

            <Group
              wrap="nowrap"
              gap={"xs"}
              bg={"gray.1"}
              style={{ borderRadius: 12 }}
              p={"md"}
            >
              <Stack w={250} gap={4}>
                <Group>
                  <ThemeIcon color="orange" variant="subtle">
                    <IconEmail size={24} />
                  </ThemeIcon>
                  <Text mr={8} fw={600}>
                    {lang.pricing.emails}
                  </Text>
                </Group>
                <Text size="xs" c={"dimmed"}>
                  {lang.pricing.premium.email_desc}
                </Text>
              </Stack>
              <NumberInput
                value={defaultValues?.email || email * 5}
                onChange={(v) => {
                  if (v === 0) {
                    return setEmail(1);
                  }
                  setDefaultValues((prev) => ({ ...prev, email: 0 }));
                  setEmail(v / 5);
                  // Check if same value, then remove it in edited
                  if (!defaultValuesStart?.email) {
                    return setEdited((prev) => [...prev, "email"]);
                  }
                  if (
                    defaultValuesStart?.email &&
                    v !== defaultValuesStart?.email
                  ) {
                    return setEdited((prev) => [...prev, "email"]);
                  }
                  if (v === defaultValuesStart?.email) {
                    setEdited((prev) => prev.filter((x) => x !== "email"));
                  }
                }}
                onBlur={(e) => {
                  let v = parseInt(e.target.value);

                  setEmail(Math.ceil(v / 5));
                }}
                step={5}
                w={100}
                ml={"auto"}
                min={0}
                max={1000}
                size="md"
                clampBehavior="strict"
              />
            </Group>

            <Group
              wrap="nowrap"
              gap={"xs"}
              bg={"gray.1"}
              style={{ borderRadius: 12 }}
              p={"md"}
            >
              <Stack w={300} gap={4}>
                <Group>
                  <ThemeIcon color="tertiary.8" variant="subtle">
                    <IconUserSearch size={24} />
                  </ThemeIcon>
                  <Text mr={8} fw={600}>
                    {lang.pricing.enrich_monthly}
                  </Text>

                  <EnrichInfo />
                </Group>
                <Text size="xs" c={"dimmed"}>
                  {lang.pricing.enrich_text}
                </Text>
              </Stack>
              <NumberInput
                value={defaultValues?.enrich || enrich * 1000}
                onChange={(v) => {
                  setDefaultValues((prev) => ({ ...prev, enrich: 0 }));
                  setEnrich(v / 1000);
                  // Check if same value, then remove it in edited
                  if (
                    (defaultValuesStart?.enrich &&
                      v !== defaultValuesStart?.enrich) ||
                    !defaultValuesStart?.enrich
                  ) {
                    return setEdited((prev) => [...prev, "enrich"]);
                  }
                  if (v === defaultValuesStart?.enrich) {
                    setEdited((prev) => prev.filter((x) => x !== "enrich"));
                  }
                }}
                w={100}
                size="md"
                ml={"auto"}
                step={1000}
                min={0}
                max={1000000}
                onBlur={(e) => {
                  let v = parseInt(e.target.value);

                  setEnrich(Math.ceil(v / 1000));
                }}
              />
            </Group>

            <Group
              wrap="nowrap"
              gap={"xs"}
              bg={"gray.1"}
              style={{ borderRadius: 12 }}
              p={"md"}
            >
              <Stack w={300} gap={4}>
                <Group>
                  <ThemeIcon color="tertiary.8" variant="subtle">
                    <IconDeviceMobile size={24} />
                  </ThemeIcon>
                  <Text mr={8} fw={600}>
                    {lang.settings.mobile_phone_per_month}
                  </Text>
                </Group>
                <Text size="xs" c={"dimmed"}>
                  {lang.settings.mobile_phone_text}
                </Text>
              </Stack>
              <NumberInput
                value={defaultValues?.mobile || mobile * 100}
                onChange={(v) => {
                  setDefaultValues((prev) => ({ ...prev, mobile: 0 }));
                  setMobile(v / 100);
                  // Check if same value, then remove it in edited
                  if (
                    (defaultValuesStart?.mobile &&
                      v !== defaultValuesStart?.mobile) ||
                    !defaultValuesStart?.mobile
                  ) {
                    return setEdited((prev) => [...prev, "mobile"]);
                  }
                  if (v === defaultValuesStart?.mobile) {
                    setEdited((prev) => prev.filter((x) => x !== "mobile"));
                  }
                }}
                w={100}
                size="md"
                ml={"auto"}
                step={100}
                min={0}
                max={1000000}
                onBlur={(e) => {
                  let v = parseInt(e.target.value);

                  setMobile(Math.ceil(v / 100));
                }}
              />
            </Group>
          </Stack>

          <Stack ml={"auto"} mr={16}>
            {lang.pricing.premium.features.map((item, i) => (
              <Group key={i}>
                <ThemeIcon radius={"md"} variant="filled" color="green">
                  <IconCheck size={20} />
                </ThemeIcon>

                <Text>{item}</Text>
              </Group>
            ))}
          </Stack>
        </Group>

        {/* Bottom */}
        <Group mt={8} gap={"lg"}>
          <Price
            defaultPrice={defaultPrice}
            futurePrice={price}
            reccurence={reccurence}
            active={profile?.subscriptions?.active}
            subscription_id={profile?.subscriptions?.subscription_id}
          />

          <Button
            size="xl"
            variant="gradient"
            rightSection={
              profile?.subscriptions.subscription_id ? (
                <IconArrowRight />
              ) : (
                <IconShoppingCartCheck />
              )
            }
            ml={price !== defaultPrice ? 0 : "auto"}
            radius={"md"}
            fw={700}
            onClick={handleCheckout}
            disabled={
              edited.length === 0 || linkedin + email + enrich + mobile === 0
            }
          >
            {profile?.subscriptions.subscription_id
              ? lang.pricing.next
              : lang.pricing.validate}
          </Button>
        </Group>
      </Paper>
    </Stack>
  );
};

const EnrichInfo = () => {
  const lang = useLanguage((s) => s.language);

  return (
    <HoverCard position="top">
      <HoverCard.Target>
        <ThemeIcon variant="subtle">
          <IconInfoCircle />
        </ThemeIcon>
      </HoverCard.Target>
      <HoverCard.Dropdown>
        <Stack maw={300}>
          <Text>{lang.pricing.premium.enrich_info}</Text>
          <Text>{lang.pricing.premium.enrich_info2}</Text>
        </Stack>
      </HoverCard.Dropdown>
    </HoverCard>
  );
};

export default PaidElement;
